import { useCallback } from "react";
import { electronModule } from "@/modules/electron";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { clientEnvModule } from "@/modules/client-env";
import {
  RecallPermissionKind,
  RecallPermissionMap,
  RecallPermissionStatus,
} from "@/modules/electron/types";

/**
 * On mount, synchronizes OS/Electron permission state (via Recall) with the
 * MobX `audioCapture` store and emits a custom event so other listeners can react.
 *
 * Behavior:
 * - Non-desktop (web): assumes microphone permission is effectively allowed by the browser
 *   flow and sets Accessibility/Screen Capture to false to avoid desktop-only nags.
 * - Desktop (Electron): queries current permission status and mirrors it into the store;
 *   subscribes to Recall updates and refreshes the status when it changes.
 *
 * Side effects:
 * - Drives the visibility of the system audio permission modal through
 *   `store.audioCapture.syncRecallPermissions`.
 */
const filterUndefinedPermissions = (
  input?: RecallPermissionMap
): RecallPermissionMap | undefined => {
  if (!input) return undefined;
  const entries = Object.entries(input).filter(([, value]) => value !== undefined);
  return entries.length > 0 ? (Object.fromEntries(entries) as RecallPermissionMap) : {};
};

export const useSystemAudioPermissionSynchronizer = (): void => {
  const { store } = useAppStore();

  const syncPermissions = useCallback(
    async (incomingStatus?: RecallPermissionMap) => {
      try {
        let status = filterUndefinedPermissions(incomingStatus);

        if (!status || Object.keys(status).length === 0) {
          const latest = await electronModule.recall.getPermissionStatus();
          status = filterUndefinedPermissions(latest) ?? status;
        } else {
          const trackedKeys: RecallPermissionKind[] = [
            RecallPermissionKind.ScreenCapture,
            RecallPermissionKind.Microphone,
          ];
          const isMissingTrackedPermission = trackedKeys.some(key => status?.[key] === undefined);

          if (isMissingTrackedPermission) {
            const latest = await electronModule.recall.getPermissionStatus();
            if (latest) {
              const filteredLatest = filterUndefinedPermissions(latest);
              if (filteredLatest) {
                status = { ...filteredLatest, ...status };
              }
            }
          }
        }

        await store.audioCapture.syncRecallPermissions(status ?? {});
      } catch (error) {
        logger.error({
          message: "Failed to sync Recall permissions",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }
    },
    [store.audioCapture]
  );

  /**
   * Initialize permission state and subscribe to changes.
   */
  useEffectOnMount(() => {
    if (!clientEnvModule.isDesktop()) {
      void syncPermissions({
        [RecallPermissionKind.ScreenCapture]: RecallPermissionStatus.NotRequested,
        [RecallPermissionKind.Microphone]: RecallPermissionStatus.Granted,
      });
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const asyncLogic = async () => {
      await syncPermissions();
      // Subscribe to permission updates if available
      try {
        unsubscribe = await electronModule.recall.onPermissionStatus?.({
          callback: () => {
            void syncPermissions();
          },
        });
      } catch (_) {
        /* empty */
      }
    };

    void asyncLogic();

    return () => {
      try {
        unsubscribe?.();
      } catch (_) {
        /* empty */
      }
    };
  });
};
